import { type ApolloCache, type MutationFunction } from '@apollo/client';
import { GET_VISIBLE_FOLDERS_BY_FOLDER_TYPE_QUERY, type GetVisibleFoldersFromFolderOutput, type DeleteFolderOutput, type SelfUnsubscribeMutationOutput } from 'graphql/folder';
import type { FolderType, Link, SharedFolder, StateSetter } from 'types';
import { FormattedMessage } from 'react-intl';
import { trackEvent, GAEventCategory, GAShareEventActions, GAShareEventLabels } from 'services/googleAnalytics';
import {type ShowSnackbarFunction} from 'contexts/SnackbarContext';

type GetUpdateCacheFunction = {
  selectedFileType: FolderType;
  selectedFolder?: SharedFolder;
  selectedLink?: Link;
  setSelectedFolder: StateSetter<SharedFolder | undefined>;
  setShowDeleteOrQuitModal: StateSetter<boolean>;
};

export const getUpdateCacheFunction =
  ({
    selectedFileType,
    selectedFolder,
    selectedLink,
    setSelectedFolder,
    setShowDeleteOrQuitModal,
  }: GetUpdateCacheFunction) =>
  (cache: ApolloCache<any>): void => {
    const variables = {
      getVisibleFoldersByFolderTypeInput: {
        channel: selectedLink?.channelKey,
        folderType: selectedFileType,
      },
    };

    const data =
      cache.readQuery<GetVisibleFoldersFromFolderOutput>({
        query: GET_VISIBLE_FOLDERS_BY_FOLDER_TYPE_QUERY,
        variables,
      });

    const updatedData = {
      ...data,
      visibleFoldersFromFolderType: {
        ...data?.visibleFoldersFromFolderType,
        folders: data?.visibleFoldersFromFolderType.folders.filter(
          (folder) => folder.id !== selectedFolder?.id,
        ),
      },
    };

    cache.writeQuery({
      data: updatedData,
      query: GET_VISIBLE_FOLDERS_BY_FOLDER_TYPE_QUERY,
      variables,
    });

    setShowDeleteOrQuitModal(false);
    setSelectedFolder(undefined);
  };

type GetHandleValidateFunction = {
  isFolderAdmin: boolean;
  selectedLink?: Link;
  selectedFolder?: SharedFolder;
  deleteFolder: MutationFunction<DeleteFolderOutput>;
  selfUnsubscribe: MutationFunction<SelfUnsubscribeMutationOutput>;
  errorSnackbar: ShowSnackbarFunction;
  successSnackbar: ShowSnackbarFunction;
};

export const getHandleValidateFunction =
  ({
    isFolderAdmin,
    selectedLink,
    selectedFolder,
    deleteFolder,
    selfUnsubscribe,
    errorSnackbar,
    successSnackbar,
  }: GetHandleValidateFunction) =>
  (): void => {
    const input = {
      channel: selectedLink?.channelKey,
      folderKey: selectedFolder?.key,
    };

    if (isFolderAdmin) {
      deleteFolder({
        variables: {
          deleteFolderInput: input,
        },
      })
        .then(({ data }) => {
          if (data?.deleteFolder.isDeleted) {
            trackEvent(
              GAEventCategory.SHARE,
              GAShareEventActions.DELETE_FOLDER,
              selectedFolder?.sharedWith.length || 0 > 1
                ? GAShareEventLabels.SHARED
                : GAShareEventLabels.NOT_SHARED,
            );

            successSnackbar(
              <FormattedMessage id="share.folder.delete.result.success" />,
            );
          } else {
            errorSnackbar(
              <FormattedMessage id="share.folder.delete.result.error" />,
            );
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      selfUnsubscribe({
        variables: {
          selfUnsubscribeInput: input,
        },
      })
        .then(({ data }) => {
          if (data?.selfUnsubscribe.folder) {
            trackEvent(
              GAEventCategory.SHARE,
              GAShareEventActions.QUIT_SHARED_FOLDER,
              GAShareEventActions.QUIT_SHARED_FOLDER,
            );

            successSnackbar(
              <FormattedMessage id="share.folder.quit.result.success" />,
            );
          } else {
            errorSnackbar(
              <FormattedMessage id="share.folder.quit.result.error" />,
            );
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  };

type GetModalMessagesIdInputType = {
  isFolderAdmin: boolean;
};

type GetModalMessagesIdOutputType = {
  descriptionMessageId: string;
  titleMessageId: string;
};

export const getModalMessagesId = ({
  isFolderAdmin,
}: GetModalMessagesIdInputType): GetModalMessagesIdOutputType =>
  isFolderAdmin
    ? {
        descriptionMessageId: 'share.folder.delete.description',
        titleMessageId: 'share.folder.settings.deleteFolder',
      }
    : {
        descriptionMessageId: 'share.folder.quit.description',
        titleMessageId: 'share.folder.settings.quitFolder',
      };
